import { STATUS_COLORS, FALLBACK_COLORS } from 'src/components/dashboard/DashboardConstants';

/**
 * Returns a consistent color for a given workflow status.
 * Falls back to a rotating palette for unknown statuses.
 */
export function getStatusColor(status: string, index: number): string {
	return STATUS_COLORS[status.toUpperCase()] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

/**
 * Extracts all unique status names (excluding TOTAL) from workflow items.
 */
export function collectStatuses(
	items: Array<{ charts: { byStatus: Array<{ status: string }> } }>,
): string[] {
	return [...new Set(
		items.flatMap((item) => item.charts.byStatus.map((entry) => entry.status))
			.filter((status) => status !== 'TOTAL'),
	)];
}

/**
 * Builds a stacked bar chart series array from workflow items.
 * Each status becomes one series, with one data point per workflow.
 */
export function buildStatusBarChartData(
	items: Array<{ workflowType: string; charts: { byStatus: Array<{ status: string; count: number }> } }>,
	statuses: string[],
	getLabel: (workflowId: string) => string,
) {
	return statuses.map((status, index) => ({
		title: status,
		type: 'bar' as const,
		data: items.map((workflow) => ({
			x: getLabel(workflow.workflowType),
			y: workflow.charts.byStatus.find((entry) => entry.status === status)?.count ?? 0,
		})),
		color: getStatusColor(status, index),
	}));
}

/**
 * Aggregates status counts across multiple workflow items (excluding TOTAL).
 * Returns a total and pie chart-ready data array.
 */
export function buildStatusPieChartData(
	items: Array<{ charts: { byStatus: Array<{ status: string; count: number }> } }>,
) {
	const countsByStatus = new Map<string, number>();

	items.flatMap((workflow) => workflow.charts.byStatus)
		.filter((entry) => entry.status !== 'TOTAL')
		.forEach((entry) => countsByStatus.set(entry.status, (countsByStatus.get(entry.status) || 0) + entry.count));

	let total = 0;
	const pieData = Array.from(countsByStatus, ([status, count], index) => {
		total += count;
		return { title: status, value: count, color: getStatusColor(status, index) };
	}).filter((entry) => entry.value > 0);

	return { total, pieData };
}

/**
 * Merges items with the same key, summing their counts.
 * Useful for aggregating region/country/awsRegion data across multiple workflows.
 */
export function mergeCountsByKey<T extends { count: number }>(
	items: T[],
	keyField: keyof T,
): Array<{ [K in keyof T]: T[K] }> {
	const mergedCounts = new Map<string, number>();

	items.forEach((item) => {
		const key = String(item[keyField]);
		mergedCounts.set(key, (mergedCounts.get(key) || 0) + item.count);
	});

	return Array.from(mergedCounts, ([key, count]) => ({ [keyField]: key, count } as any));
}
